import { useState, useCallback, useEffect } from 'react';
import { useTradesSearch } from '@/hooks/useTradesSearch';
import { requestQuote } from '@/lib/supabase/quotes';
import { createJobWithQuotes } from '@/lib/supabase/jobs-quotes';
import { useAuth } from '@/contexts/AuthContext';
import { detectTradeCategory } from '@/lib/tradeCategories';
import { toast } from "sonner";
import { debug } from "@/lib/utils";
import {
  addToNotificationQueue,
  processNextNotification,
  getNotificationQueueLength,
  clearNotificationQueue
} from '@/lib/supabase/notification';
import { useProfile } from '@/hooks/useProfile';

/**
 * Hook for managing quote requests
 * @param jobId Optional job ID to associate with the quote request
 * @param isStandalone Whether this is the standalone SmartQuote (true) or within a job (false)
 * @returns Functions and state for managing quote requests
 */
export function useTradeQuoteRequest(jobId?: string, isStandalone: boolean = false) {
  const { user } = useAuth();
  const { profile: userProfile } = useProfile();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTradespeople, setSelectedTradespeople] = useState<string[]>([]);
  const [message, setMessage] = useState('');
  const [detectedCategory, setDetectedCategory] = useState<{category: string, matchedKeyword: string} | null>(null);

  // Initialize email with the authenticated user's email if available
  const [email, setEmail] = useState(user?.email || '');

  // Update email when user auth state changes
  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
  }, [user, email]);

  // Job creation fields - only used for standalone SmartQuote
  const [jobTitle, setJobTitle] = useState('');
  const [createdJobId, setCreatedJobId] = useState<string | null>(null);

  // Reuse the trades search hook
  const { tradespeople, searchTradespeople, isSearching } = useTradesSearch(user?.id || '');

  // Function to find category based on text
  const findCategory = useCallback((text: string) => {
    if (!text) return null;

    debug.log(`Finding category for text: "${text}"`);

    // Use the centralized detection function from tradeCategories.ts
    const categoryObj = detectTradeCategory(text);

    if (categoryObj) {
      // Get the matched keyword for debugging
      const lowercaseText = text.toLowerCase();
      let matchedKeyword = '';

      // Find which keyword matched
      for (const keyword of categoryObj.keywords) {
        if (lowercaseText.includes(keyword.toLowerCase())) {
          matchedKeyword = keyword;
          break;
        }
      }

      const result = {
        category: categoryObj.id,
        matchedKeyword: matchedKeyword
      };

      debug.log(`Found category "${categoryObj.id}" matching keyword "${matchedKeyword}"`);
      setDetectedCategory(result);
      return result;
    }

    debug.log(`No category found for text: "${text}"`);
    setDetectedCategory(null);
    return null;
  }, []);

  // Update job title based on detected category
  useEffect(() => {
    if (detectedCategory?.category && isStandalone) {
      const categoryName = detectedCategory.category
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

      setJobTitle(`${categoryName} Work`);
    }
  }, [detectedCategory, isStandalone]);

  // Generate message template based on text
  const generateMessageTemplate = useCallback((text: string) => {
    const userPostcode = userProfile?.postcode;
    const userName = userProfile?.username || userProfile?.email?.split('@')[0] || '';
    const jobType = detectedCategory?.category
      ?.replace(/_/g, ' ')
      ?.split(' ')
      ?.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      ?.join(' ') || 'some work done';

    return `Hi,

I got your details from neighbors in ${userPostcode || '[your postcode]'}.

I need ${jobType}:
${text}

Are you available to take a look and what would you need to know?

Thanks,
${userName || '[Your name]'}`;
  }, [userProfile, detectedCategory]);

  // Handle trade selection
  const handleTradeSelection = useCallback((tradeId: string) => {
    setSelectedTradespeople(prev =>
      prev.includes(tradeId)
        ? prev.filter(id => id !== tradeId)
        : [...prev, tradeId]
    );
  }, []);

  // Handle quote request submission
  const submitQuoteRequests = async (contactMethod: 'email' | 'phone' = 'email', contactEmail?: string) => {
    if (!user?.id || selectedTradespeople.length === 0) {
      toast.error('Please select at least one tradesperson');
      return false;
    }

    // Use provided contactEmail if given, otherwise fall back to the email from state
    const userEmail = contactEmail || email;

    // Validate email if using email contact method
    if (contactMethod === 'email' && (!userEmail || !/\S+@\S+\.\S+/.test(userEmail))) {
      toast.error('Please enter a valid email address');
      return false;
    }

    try {
      setIsSubmitting(true);

      // If this is the standalone SmartQuote and no job ID provided, always create a job
      if (isStandalone && !jobId) {
        // Create new job with quotes but don't attach a tradesperson directly
        const result = await createJobWithQuotes(user.id, {
          title: jobTitle || 'Quote Request',
          description: message,
          category: detectedCategory?.category,
          // No primary tradesperson attached to job - that will happen when a quote is accepted
          trade_ids: selectedTradespeople
        });

        // Store the created job ID for navigation
        setCreatedJobId(result.job.id);

        // Add all notifications to the queue instead of sending immediately
        let queueCount = 0;
        for (const quote of result.quotes) {
          queueCount = addToNotificationQueue(quote, contactMethod, message, userEmail);
        }

        // Process the first notification with error handling
        try {
          const processResult = await processNextNotification();
          if (!processResult) {
            debug.warn('First notification processing returned false');
          }
        } catch (notificationError) {
          debug.error('Error processing first notification:', notificationError);
          toast.warning('Quote requests created but there was an issue sending notifications');
        }

        const remainingCount = queueCount - 1;
        if (remainingCount > 0) {
          toast.success(`Job created with ${result.quotes.length} quotes. ${remainingCount} notifications queued.`);
        } else {
          toast.success('Job created and quote request sent');
        }
      } else {
        // Just create quote requests for an existing job
        const quotePromises = selectedTradespeople.map(async (tradeId) => {
          try {
            debug.log(`Requesting quote for trade ${tradeId}${jobId ? ` for job ${jobId}` : ''}`);
            return await requestQuote(user.id, {
              trade_id: tradeId,
              ...(jobId ? { job_id: jobId } : {})
            });
          } catch (error) {
            debug.error(`Failed to create quote for trade ${tradeId}:`, error);
            toast.error(`Failed to create quote request for one tradesperson`);
            return null;
          }
        });

        // Wait for all quotes to be created (some may be null if they failed)
        const quotes = (await Promise.all(quotePromises)).filter(Boolean);

        // Check if any quotes were successfully created
        if (quotes.length === 0) {
          throw new Error('Failed to create any quote requests');
        }

        // Add all notifications to the queue
        let queueCount = 0;
        for (const quote of quotes) {
          if (quote) {
            queueCount = addToNotificationQueue(quote, contactMethod, message, userEmail);
          }
        }

        // Process the first notification with error handling
        try {
          const processResult = await processNextNotification();
          if (!processResult) {
            debug.warn('First notification processing returned false');
          }
        } catch (notificationError) {
          debug.error('Error processing first notification:', notificationError);
          toast.warning('Quote requests created but there was an issue sending notifications');
        }

        const remainingCount = queueCount - 1;
        if (remainingCount > 0) {
          toast.success(`${quotes.length} quote requests created. ${remainingCount} notifications queued.`);
        } else {
          toast.success('Quote request sent successfully');
        }
      }

      // Reset form
      setSelectedTradespeople([]);
      setJobTitle('');

      return true;
    } catch (error) {
      debug.error('Error requesting quotes:', error);
      toast.error('Failed to send quote requests');
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get the current notification queue length for the UI
  const [pendingNotifications, setPendingNotifications] = useState(getNotificationQueueLength());

  // Process the next notification in the queue
  const sendNextNotification = async () => {
    const result = await processNextNotification();
    // Update pending count after processing
    setPendingNotifications(getNotificationQueueLength());
    return result;
  };

  // Clear all pending notifications
  const clearNotifications = () => {
    clearNotificationQueue();
    setPendingNotifications(0);
  };

  // Update the pending count when the component mounts
  useEffect(() => {
    setPendingNotifications(getNotificationQueueLength());
  }, []);

  return {
    isSubmitting,
    isSearching,
    tradespeople,
    selectedTradespeople,
    message,
    setMessage,
    email,
    setEmail,
    searchTradespeople,
    findCategory,
    generateMessageTemplate,
    handleTradeSelection,
    submitQuoteRequests,
    resetSelection: () => setSelectedTradespeople([]),
    // Job creation fields
    jobTitle,
    setJobTitle,
    createdJobId,
    detectedCategory,
    // Notification queue functionality
    pendingNotifications,
    sendNextNotification,
    clearNotifications
  };
}
